import React from 'react';
import { TableRow, TableCell, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { colors, fonts, spacing, tableStyles } from '../../constants';
import { getLifecycleStatusMeta, getPriorityColor, truncateText, getLatestResponseInfo } from '../../utils/allQueriesUtils';
import { normalizeLifecycleStatus } from '../../utils/queryListUtils';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * AllQueryTableRow Component
 * Individual query row in table
 */
const AllQueryTableRow = ({
  query,
  onView,
}) => {
  const primaryStatus = normalizeLifecycleStatus(query.statusLabel);
  const secondaryStatus = normalizeLifecycleStatus(query.status);
  const lifecycleMeta =
    getLifecycleStatusMeta(primaryStatus) ||
    getLifecycleStatusMeta(secondaryStatus);
  
  const { message, respondedAt, responderName } = getLatestResponseInfo(query);

  return (
    <TableRow 
      key={query._id} 
      hover 
      sx={{ 
        backgroundColor: colors.backgroundWhite,
        '&:hover': { 
          backgroundColor: '#0e172a15',
          borderLeft: '3px solid #0e172a',
        },
        '&:nth-of-type(even)': {
          backgroundColor: colors.backgroundLight,
        },
      }}
    >
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{query.queryId || query._id}</TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {lifecycleMeta ? (
            <Chip
              label={lifecycleMeta.label}
              size="small"
              sx={{
                backgroundColor: `${lifecycleMeta.color}20`,
                color: lifecycleMeta.color,
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeXs,
              }}
            />
          ) : null}
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>
        <Chip
          label={query.priority?.toUpperCase()}
          size="small"
          sx={{
            backgroundColor: getPriorityColor(query.priority) + '20',
            color: getPriorityColor(query.priority),
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeXs,
          }}
        />
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>
        {!message ? (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: fonts.fontSizeXs,
              fontStyle: 'italic',
            }}
          >
            No response yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#1b2538',
                fontSize: fonts.fontSizeXs,
                lineHeight: 1.3,
              }}
            >
              {truncateText(message, 80)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontSize: fonts.fontSizeXs,
              }}
            >
              {[
                responderName ? `by ${responderName}` : null,
                
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{query.raisedBy?.name || 'N/A'}</TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{query.assignee?.name || 'N/A'}</TableCell>
      <TableCell sx={{ ...tableStyles.cell, color: '#64748b' }}>{formatDateTime(query.raisedAt)}</TableCell>
      <TableCell sx={{ ...tableStyles.cell }}>
        <Box sx={{ display: 'flex', gap: spacing.xs }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => onView(query)}
              sx={{
                color: '#0e172a',
                '&:hover': { 
                  backgroundColor: '#0e172a15',
                  color: '#0e172a',
                },
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default AllQueryTableRow;
